/**
 * RouteViewerModal — Visualização completa duma rota em modal.
 *
 * Reutiliza os mesmos componentes do Dashboard admin:
 *   - RouteMap (mapa + polyline + POIs + toggle satélite/topo)
 *   - ElevationChart (perfil de elevação)
 *   - POIManager (lista de POIs, read-only)
 *   - RouteRatings (avaliações)
 *
 * A rota é carregada via `GET /api/municipio/routes/{id}/detail` que devolve
 * o mesmo formato de `/api/routes/{id}` mas contorna restrições de grupo
 * quando a rota está associada ao município do utilizador.
 *
 * Props:
 *   routeId    string — required
 *   open       bool
 *   onClose    () => void
 *   user       user obj (usado por POIManager / RouteRatings)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import RouteMap from '@/components/RouteMap';
import ElevationChart from '@/components/ElevationChart';
import POIManager from '@/components/POIManager';
import RouteRatings from '@/components/RouteRatings';
import { Loader2, MapPin, TrendingUp, Route as RouteIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RouteViewerModal = ({ routeId, open, onClose, user, liveOverlay = false, liveOverlayMode = 'municipio', companyId = null }) => {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusPOI, setFocusPOI] = useState(null);

  useEffect(() => {
    if (!open || !routeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Endpoint depende do modo:
        //   municipio → /api/municipio/routes/{id}/detail (contorna restrições
        //               de grupo quando a rota está associada ao concelho)
        //   company   → /api/routes/{id} (já respeita company_id no backend)
        const endpoint = liveOverlayMode === 'company'
          ? `${API}/routes/${routeId}`
          : `${API}/municipio/routes/${routeId}/detail`;
        const { data } = await axios.get(endpoint, { withCredentials: true });
        if (!cancelled) setRoute(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro a carregar rota');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, routeId, liveOverlayMode]);

  const stat = (icon, label, value) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-500">{icon}</span>
      <span className="font-semibold text-slate-800">{label}:</span>
      <span className="text-slate-700">{value}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="route-viewer-modal"
      >
        <DialogHeader className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="flex items-center gap-2 text-base">
            <RouteIcon className="w-5 h-5 text-emerald-700" />
            {route?.name || 'A carregar…'}
          </DialogTitle>
          {route && (
            <div className="flex flex-wrap gap-4 mt-1">
              {route.distance_km != null && stat('📏', 'Distância', `${Number(route.distance_km).toFixed(1)} km`)}
              {route.elevation_gain_m != null && stat('⛰️', 'Subida', `${Math.round(route.elevation_gain_m)} m`)}
              {route.duration_hours != null && stat('⏱️', 'Duração', `${Number(route.duration_hours).toFixed(1)} h`)}
              {route.difficulty && stat('🎯', 'Dificuldade', route.difficulty)}
              {(route.pois?.length || 0) > 0 && stat('📍', 'POIs', route.pois.length)}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
            </div>
          )}
          {!loading && route && (
            <>
              {/* Map */}
              <div className="flex-1 min-h-0 relative" data-testid="viewer-map">
                <RouteMap route={route} focusPOI={focusPOI} liveOverlay={liveOverlay} liveOverlayMode={liveOverlayMode} companyId={companyId} />
              </div>

              {/* Bottom tabs (elevation / ratings / pois) */}
              <div className="border-t border-slate-200 bg-white flex-shrink-0">
                <Tabs defaultValue={route.elevation_profile?.length > 0 ? 'elevation' : 'pois'}>
                  <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 px-2 h-10">
                    {route.elevation_profile && route.elevation_profile.length > 0 && (
                      <TabsTrigger value="elevation" data-testid="viewer-tab-elevation">
                        <TrendingUp className="w-4 h-4 mr-1" /> Elevação
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="pois" data-testid="viewer-tab-pois">
                      <MapPin className="w-4 h-4 mr-1" /> POIs {(route.pois?.length || 0) > 0 ? `(${route.pois.length})` : ''}
                    </TabsTrigger>
                    <TabsTrigger value="ratings" data-testid="viewer-tab-ratings">
                      ⭐ Avaliações
                    </TabsTrigger>
                  </TabsList>
                  {route.elevation_profile && route.elevation_profile.length > 0 && (
                    <TabsContent value="elevation" className="p-4 max-h-[28vh] overflow-y-auto m-0">
                      <ElevationChart
                        elevationProfile={route.elevation_profile}
                        distance={route.distance_km}
                        elevationGain={route.elevation_gain_m}
                      />
                    </TabsContent>
                  )}
                  <TabsContent value="pois" className="p-4 max-h-[28vh] overflow-y-auto m-0">
                    <POIManager
                      route={route}
                      isAdmin={false}
                      onPOIsUpdated={() => {}}
                      onPOIFocus={(poi) => setFocusPOI({ ...poi, _t: Date.now() })}
                    />
                  </TabsContent>
                  <TabsContent value="ratings" className="p-4 max-h-[28vh] overflow-y-auto m-0">
                    <RouteRatings route={route} user={user} />
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RouteViewerModal;
